export class ApiWrapper {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.errorStatusCodes = [401, 403, 404, 409, 422, 500];
  }

  async fetchWithAuth(endpoint, method, body, signal) {
    const accessToken = localStorage.getItem("access_token");

    if (!accessToken) {
      throw new Error("Access token not found.");
    }

    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${accessToken}`,

          ...(body ? { "Content-Type": "application/json" } : {}),
        },
        redirect: "follow",
        body: body ? JSON.stringify(body) : undefined,
        signal, // Pass the signal to fetch
      });

      if (!response.ok) {
        if (this.errorStatusCodes.includes(response.status)) {
          return { status: response.status };
        }
        const errorResponse = await response.json();
        const errorDetail = errorResponse?.detail || "Unknown error";
        throw new Error(errorDetail);
      }

      const data = await response.json();

      return { data, status: response.status };
    } catch (error) {
      console.error("Error during API request:", error); // More detailed error logging
      if (error.name === "AbortError") {
        return { error: new Error("Request canceled") };
      }
      return { error: error.message || error }; // Return the error message for easier debugging
    }
  }

  async streamWithAuth(endpoint, body, onMessage, onError, onComplete, signal) {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) throw new Error("Access token not found.");

    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,

          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal,
      });

      const messageId = response.headers.get("X-Message-Id") || null;

      if (!response.body) throw new Error("No response body");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = ""; // Buffer to hold incomplete words

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        buffer += chunk; // Append chunk to buffer
        // Find the last complete word break (space or newline)
        const lastBreakIndex = buffer.lastIndexOf(" ");
        if (lastBreakIndex === -1) continue; // No full word yet

        const completeText = buffer.slice(0, lastBreakIndex + 1); // Extract complete words
        buffer = buffer.slice(lastBreakIndex + 1); // Keep remainder for next iteration

        buffer = "WORD: " + buffer;

        const words = completeText
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.startsWith("WORD: ")) // Keep only relevant lines
          .map((line) => line.replace("WORD: ", "")); // Remove prefix

        if (buffer.trim() === "WORD: {}") {
          buffer = ""; // Reset buffer if it's empty
        }
        if (words.length) {
          onMessage(words.join("") + " "); // Send words with space
        }
      }

      // Process any remaining buffered content
      if (buffer.trim().length > 0) {
        const lastWords = buffer
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.startsWith("WORD: "))
          .map((line) => line.replace("WORD: ", ""));

        if (lastWords.length) {
          onMessage(lastWords.join(" ") + " ");
        }
      }

      if (onComplete) {
        onComplete(messageId); // Pass x-message-id to onComplete
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("SSE request aborted");
      } else {
        onError(error);
      }
    }
  }

  async get(endpoint, signal) {
    return this.fetchWithAuth(endpoint, "GET", undefined, signal);
  }

  async post(endpoint, body, signal) {
    return this.fetchWithAuth(endpoint, "POST", body, signal);
  }

  async patch(endpoint, body, signal) {
    return this.fetchWithAuth(endpoint, "PATCH", body, signal);
  }

  async delete(endpoint, signal) {
    return this.fetchWithAuth(endpoint, "DELETE", undefined, signal);
  }
}
